import { Search } from 'lucide-react';
import { SearchField } from '@heroui/react/search-field';
import { cn } from '@/utils/cn';

/**
 * Search input — HeroUI's SearchField.
 *
 * One component for every "search this list" box. Previously each screen
 * rolled its own: some used a raw `<input>` with an absolutely-positioned
 * icon and `pl-9` (which overlaps the icon in RTL), some used `Input` with
 * `startIcon`, and each picked its own height, border and placeholder
 * colour. Clearing was a hand-written `<button>` where it existed at all.
 *
 * SearchField gives the correct `type="search"` semantics, a built-in clear
 * button, and Escape-to-clear.
 *
 * `onChange` receives the string value directly — not an event — since every
 * call site only ever read `e.target.value`.
 */
export interface SearchInputProps {
  value?: string;
  defaultValue?: string;
  /** Called with the new query string. */
  onChange?: (value: string) => void;
  /** Fired on Enter, for searches that hit the server rather than filtering. */
  onSubmit?: (value: string) => void;
  placeholder?: string;
  /** Hidden from view but read by screen readers. Defaults to the placeholder. */
  'aria-label'?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  name?: string;
  id?: string;
  className?: string;
}

export function SearchInput({
  value,
  defaultValue,
  onChange,
  onSubmit,
  placeholder = 'Search…',
  autoFocus,
  disabled,
  className,
  ...props
}: SearchInputProps) {
  return (
    <SearchField
      value={value}
      defaultValue={defaultValue}
      onChange={onChange}
      onSubmit={onSubmit}
      isDisabled={disabled}
      aria-label={props['aria-label'] ?? placeholder}
      className={cn('w-full', className)}
      {...props}
    >
      <SearchField.Group>
        <SearchField.SearchIcon>
          <Search />
        </SearchField.SearchIcon>
        {/* min-w-0: a flex item will not shrink below its intrinsic width, so
            in a narrow (or RTL) toolbar the input pushed the clear button out
            of the field. */}
        <SearchField.Input
          className="min-w-0"
          placeholder={placeholder}
          autoFocus={autoFocus}
        />
        <SearchField.ClearButton />
      </SearchField.Group>
    </SearchField>
  );
}
